import { MapPin, ShieldAlert, TriangleAlert, UserPlus } from 'lucide-react';
import { SOVEREIGN } from '../../utils/epcProjectVisual';
import SovereignModal from '../create/components/SovereignModal';

export type TeamDialogState =
  | {
      kind: 'error';
      title: string;
      message: string;
      /** Optional labelled facts shown under the message (Engineer / Department / Manager…). */
      context?: { label: string; value: string }[];
    }
  | {
      kind: 'confirm';
      title: string;
      message: string;
      /** Existing assignments listed in the warning card. */
      assignments: string[];
      confirmLabel: string;
      onConfirm: () => void;
    };

/**
 * Sovereign-styled popup for team-hierarchy actions — conflict errors and
 * add-anyway confirmations. Rendered after the member drawer so it stacks
 * above it.
 */
export default function TeamActionDialog({
  state,
  onClose,
}: {
  state: TeamDialogState | null;
  onClose: () => void;
}) {
  if (!state) return null;

  const isError = state.kind === 'error';

  return (
    <SovereignModal
      open
      onClose={onClose}
      title={state.title}
      subtitle={isError ? 'This assignment cannot be created' : 'Please confirm before adding'}
      icon={isError ? ShieldAlert : UserPlus}
      maxWidthClass="max-w-md"
      footer={
        isError ? (
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl px-5 py-2.5 text-sm font-bold text-white transition hover:opacity-95"
            style={{ background: SOVEREIGN.btn, border: `1px solid ${SOVEREIGN.btnBorder}`, boxShadow: SOVEREIGN.btnShadow }}
          >
            Got it
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl px-4 py-2.5 text-sm font-semibold transition hover:opacity-90"
              style={{ background: SOVEREIGN.inputBg, border: `1px solid ${SOVEREIGN.inputBorder}`, color: SOVEREIGN.title }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={state.onConfirm}
              className="inline-flex items-center gap-1.5 rounded-xl px-5 py-2.5 text-sm font-bold text-white transition hover:opacity-95"
              style={{ background: SOVEREIGN.btn, border: `1px solid ${SOVEREIGN.btnBorder}`, boxShadow: SOVEREIGN.btnShadow }}
            >
              <UserPlus className="h-4 w-4" strokeWidth={2.4} />
              {state.confirmLabel}
            </button>
          </>
        )
      }
    >
      <div className="space-y-4">
        <div
          className="flex items-start gap-3 rounded-2xl p-4"
          style={
            isError
              ? { background: 'rgba(159,18,57,0.07)', border: '1px solid rgba(159,18,57,0.30)' }
              : { background: 'rgba(201,169,97,0.12)', border: '1px solid rgba(201,169,97,0.5)' }
          }
        >
          <span
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
            style={
              isError
                ? { background: 'rgba(159,18,57,0.12)', color: '#9F1239', border: '1px solid rgba(159,18,57,0.30)' }
                : { background: 'rgba(201,169,97,0.18)', color: '#8A5A08', border: '1px solid rgba(201,169,97,0.5)' }
            }
          >
            <TriangleAlert className="h-[18px] w-[18px]" strokeWidth={2.2} />
          </span>
          <p className="text-sm font-medium leading-relaxed" style={{ color: isError ? '#9F1239' : '#8A5A08' }}>
            {state.message}
          </p>
        </div>

        {isError && state.context && state.context.length > 0 ? (
          <dl className="grid grid-cols-1 gap-2 rounded-2xl p-4" style={{ background: 'rgba(252,251,246,0.9)', border: '1px solid rgba(11,59,46,0.18)' }}>
            {state.context.map((c) => (
              <div key={c.label} className="flex items-baseline justify-between gap-3">
                <dt className="shrink-0 text-[10px] font-bold uppercase tracking-[0.14em]" style={{ color: SOVEREIGN.kpiCaption }}>
                  {c.label}
                </dt>
                <dd className="min-w-0 truncate text-sm font-bold" style={{ color: SOVEREIGN.title }}>
                  {c.value}
                </dd>
              </div>
            ))}
          </dl>
        ) : null}

        {!isError && state.assignments.length > 0 ? (
          <div className="rounded-2xl p-4" style={{ background: 'rgba(252,251,246,0.9)', border: '1px solid rgba(11,59,46,0.18)' }}>
            <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.14em]" style={{ color: SOVEREIGN.title }}>
              Current assignments
            </p>
            <ul className="space-y-1.5">
              {state.assignments.map((a) => (
                <li key={a} className="flex items-center gap-2 text-sm font-medium" style={{ color: SOVEREIGN.ink }}>
                  <MapPin className="h-3.5 w-3.5 shrink-0" style={{ color: SOVEREIGN.brand }} />
                  <span className="min-w-0 truncate">{a}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </SovereignModal>
  );
}
